import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { EmailService } from '../../email-service/email.service';
import {
  EmailCompilationService,
  EmailImageDeliveryMode,
} from '../../email-service/email-compilation.service';
import { EmailMessage } from '../../email-models/email-message.interface';
import { EMAIL_MODAL_DATE_FORMATS } from '../../email-models/email-date-format.const';
import { environment } from '../../../../environments/environment';
import { BIRTHDAY_EMAIL_TEMPLATES, languageCode } from './birthday-email-templates.const';

@Component({
  selector: 'via-email-modal',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTimepickerModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: EMAIL_MODAL_DATE_FORMATS },
  ],
  templateUrl: './birthday-email-modal.html',
  styleUrl: './birthday-email-modal.scss',
})
export class BirthdayEmailModal {
  private readonly emailService = inject(EmailService);
  private readonly emailCompilationService = inject(EmailCompilationService);
  private readonly dialogRef = inject(MatDialogRef<BirthdayEmailModal>);
  private readonly dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly birthdayBannerContentId = 'birthday-card-banner';

  // Email message content
  emailMessage: EmailMessage = {
    contextKey: '',
    fsp: '',
    clientKey: 'd767d0c2-c3f9-4f20-b107-275c96d03bcc', // This should ideally come from user details or be generated
    subject: '',
    bodyType: 'HTML',
    body: '',
    from: {
      name: 'Kevin Campbell',
      email: 'tst-mdc-mfp-joe@momentum.co.za',
    },
    toRecipients: [
      {
        name: 'Ruben Ferreira',
        email: 'ruben.ferreira@momentum.co.za',
      },
    ],
    importance: 'NORMAL',
    hasAttachments: false,
    ccRecipients: [],
    bccRecipients: [],
  };

  // View state management
  isPreviewMode = false;

  // Scheduled email data
  today = new Date();
  // So 17:37 now becomes 18:00
  topOfNextHour = new Date(
    this.today.getTime() +
      60 * 60 * 1000 -
      (this.today.getTime() % (60 * 60 * 1000)),
  );
  scheduleEmail = false;
  scheduleDate = this.today;
  scheduleTime = this.topOfNextHour;

  // Banner settings
  selectedDesign = 'blue';
  readonly remoteAssetBaseUrl = environment.remoteAssetBaseUrl;
  readonly bannerOptions = [
    {
      id: 'mfp-01',
      ariaLabel: 'Momentum Financial Planning Birthday Banner 1',
      imagePath: `${this.remoteAssetBaseUrl}/email-templates/birthdays/mfp/mfp-birthday-email-banner-1.png`,
    },
    {
      id: 'mfp-02',
      ariaLabel: 'Momentum Financial Planning Birthday Banner 2',
      imagePath: `${this.remoteAssetBaseUrl}/email-templates/birthdays/mfp/mfp-birthday-email-banner-2.png`,
    },
    {
      id: 'mfp-03',
      ariaLabel: 'Momentum Financial Planning Birthday Banner 3',
      imagePath: `${this.remoteAssetBaseUrl}/email-templates/birthdays/mfp/mfp-birthday-email-banner-3.png`,
    },
    {
      id: 'mfp-04',
      ariaLabel: 'Momentum Financial Planning Birthday Banner 4',
      imagePath: `${this.remoteAssetBaseUrl}/email-templates/birthdays/mfp/mfp-birthday-email-banner-4.png`,
    },
  ];

  selectedLanguage: languageCode = 'en';
  readonly languageOptions: { code: string; label: string }[] = [
    { code: 'en', label: 'English' },
    { code: 'af', label: 'Afrikaans' },
  ];

  get selectedBannerImagePath(): string {
    const selected = this.bannerOptions.find(
      (option) => option.id === this.selectedDesign,
    );
    return selected?.imagePath ?? this.bannerOptions[0].imagePath;
  }

  constructor() {
    this.dateAdapter.setLocale('en-GB');
    this.onLanguageChange(); // Initialize email content based on default language
  }

  onLanguageChange(): void {
    // Update the email subject and body based on the selected language
    const template = BIRTHDAY_EMAIL_TEMPLATES[this.selectedLanguage];
    if (template) {
      // Get first name in title case - if no name available, default to "Valued Client" in the selected language
      const clientName = this.emailMessage.toRecipients[0]?.name || '';
      const toName = clientName
        ? clientName
            .split(' ')[0]
            .toLowerCase()
            .replace(/^\w/, (c) => c.toUpperCase())
        : this.selectedLanguage === 'en'
          ? 'Valued Client'
          : 'Gewaardeerde Kliënt';
      // If userDetails use given_name, otherwise default "Your Advisor" in selected language
      const adviserName = this.emailMessage.from.name || '';
      const fromName = adviserName
        ? adviserName
            .split(' ')[0]
            .toLowerCase()
            .replace(/^\w/, (c) => c.toUpperCase())
        : this.selectedLanguage === 'en'
          ? 'Your Advisor'
          : 'Jou Adviseur';

      this.emailMessage.subject = template.subject;
      this.emailMessage.body = template.body
        .replace('{{toName}}', toName)
        .replace('{{fromName}}', fromName);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  openPreview(): void {
    this.isPreviewMode = true;
  }

  returnToCompose(): void {
    this.isPreviewMode = false;
  }

  sendEmail(imageDeliveryMode: EmailImageDeliveryMode = 'base64'): void {
    let sent = false;

    this.emailCompilationService
      .buildEmailMessage(
        this.emailMessage,
        this.birthdayBannerContentId,
        this.selectedBannerImagePath,
        imageDeliveryMode,
      )
      .then((emailMessage) => this.emailService.sendEmail(emailMessage))
      .then(() => {
        sent = true;
        console.log('Email sent successfully');
      })
      .catch((error) => {
        console.error('Error sending email:', error);
      })
      .finally(() => {
        this.dialogRef.close({
          sent,
          from: this.emailMessage.from.email,
          to: this.emailMessage.toRecipients,
          scheduleEmail: this.scheduleEmail,
          selectedDesign: this.selectedDesign,
          selectedLanguage: this.selectedLanguage,
          imageDeliveryMode,
          subject: this.emailMessage.subject,
          message: this.emailMessage.body,
        });
      });
  }
}
